/**
 * Shared protocol between the main thread (EngineHost) and the KPV
 * AudioWorkletProcessor. This file is imported by BOTH sides — keep it free
 * of DOM and worklet-specific globals.
 *
 * Continuous control (pad position, sliders) flows through a SharedArrayBuffer
 * so touch→sound latency is one render quantum with zero GC pressure.
 * Discrete events (program load, sample triggers) flow through port.postMessage.
 */

// ---------------------------------------------------------------------------
// Control SAB layout (UI → worklet). Float32 slots.
// ---------------------------------------------------------------------------
export const enum Ctrl {
  Touch1Active = 0,
  Touch1X = 1,
  Touch1Y = 2,
  Touch2Active = 3,
  Touch2X = 4,
  Touch2Y = 5,
  FxDepth = 6, // 0..1, the FX DEPTH slider
  Hold = 7, // 0/1 — latches last touch state
  Bpm = 8,
  InputGain = 9, // linear
  OutputGain = 10, // linear
  InputMonitor = 11, // 0/1 — pass dry input when no touch
  Mute = 12, // 0/1 — "transformer": kills the dry path, FX tails pass
  FxRelease = 13, // 0..1 — level/feedback of the release echo (synced delay)
  Monitor = 14, // 0..2 — how loud you HEAR the live input; recording taps pre-monitor
  __Size = 16,
}

/** Maximum sample banks (A..H). The first 4 mirror the hardware; the rest are ours. */
export const MAX_BANKS = 8;

// ---------------------------------------------------------------------------
// Meter SAB layout (worklet → UI). Float32 slots.
// ---------------------------------------------------------------------------
export const enum Meter {
  InL = 0,
  InR = 1,
  OutL = 2,
  OutR = 3,
  BeatPhase = 4, // 0..1 within current beat
  BarPhase = 5, // 0..1 within current 4-beat bar
  Wet = 6, // current wet mix actually applied (post touch/hold/release ramp)
  CpuLoad = 7, // rough: fraction of quantum spent processing
  SampleBankState = 8, // MAX_BANKS slots (8..15): 0 empty, 1 loaded, 2 playing, 3 recording
  SamplePos = 16, // MAX_BANKS slots (16..23): playback/record position 0..1
  Beat = 24, // absolute transport beat counter (fractional) — motion replay sync
  __Size = 32,
}

// ---------------------------------------------------------------------------
// Program config as sent to the worklet (fully resolved, no lookups needed).
// ---------------------------------------------------------------------------

/** Everything the DSP needs to know about one FX slot. */
export interface SlotConfig {
  /** Unit type id — must match a key in the worklet's unit registry. */
  unit: string;
  /** Initial values for the unit's parameters, normalized 0..1. */
  params: Record<string, number>;
}

export type ModSource = 'x1' | 'y1' | 'x2' | 'y2' | 'touch1' | 'touch2';

export type MapCurve = 'lin' | 'exp' | 'invLin' | 'switch';

/** One axis→parameter modulation route. */
export interface ModRoute {
  source: ModSource;
  slot: number; // index into slots
  param: string; // unit parameter name
  min: number; // normalized target at source=0
  max: number; // normalized target at source=1
  curve: MapCurve;
}

export interface ProgramConfig {
  id: string; // e.g. "FLt.1"
  name: string;
  slots: SlotConfig[];
  routes: ModRoute[];
  /** True if this program's time-based FX lock to BPM. */
  bpmSync: boolean;
  /** Wet path engages only while touched (KPV behavior); Hold latches. */
  touchGated: boolean;
  /** Seconds for the wet→dry ramp after release (FX Release). */
  releaseTime: number;
}

// ---------------------------------------------------------------------------
// postMessage events
// ---------------------------------------------------------------------------

export type RecordSource = 'input' | 'output';

export type SliceMode = 'skip' | 'mute';

export type HostToWorklet =
  | { type: 'loadProgram'; program: ProgramConfig }
  | { type: 'sampleTrigger'; bank: number; on: boolean }
  /** beats: auto-stop after exactly N beats at the current BPM. source
   *  'output' captures post-FX (resampling); 'input' the pre-FX bus.
   *  freeLength: no auto-stop (stop manually); `beats` then only declares how
   *  many beats the take counts as — used when the recording defines the tempo. */
  | { type: 'sampleRecord'; bank: number; on: boolean; beats?: number; source?: RecordSource; freeLength?: boolean }
  | { type: 'sampleLoad'; bank: number; l: Float32Array; r: Float32Array; beats: number; loop: boolean }
  | { type: 'sampleClear'; bank: number }
  /** Slice playback: divide the sample into `slices` (8|16) windows; bit i of
   *  `mask` enables slice i. mode 'skip' (default) cuts disabled slices from
   *  time — the loop shortens; 'mute' keeps length and plays silence. Both
   *  are de-clicked. */
  | { type: 'sampleSlices'; bank: number; slices: number; mask: number; mode: SliceMode }
  | { type: 'sampleLoopMode'; bank: number; loop: boolean }
  | { type: 'ctrlFallback'; values: Float32Array } // when SAB unavailable
  /** Align this instance's transport with the session master (multi-deck sync). */
  | { type: 'setBeat'; beat: number }
  | { type: 'ping' };

export type WorkletToHost =
  | { type: 'ready' }
  | { type: 'meterFallback'; values: Float32Array }
  | { type: 'sampleRecorded'; bank: number; frames: number }
  /** 64-point peak envelope of a bank's audio, for waveform display. */
  | { type: 'sampleOverview'; bank: number; peaks: Float32Array }
  | { type: 'log'; msg: string };

/** KPV hardware MIDI CC map (from KPV Editor manual, Global CC MAP page). */
export const KPV_CC = {
  touch1X: 12,
  touch1Y: 13,
  touch1On: 92,
  touch2X: 14,
  touch2Y: 15,
  touch2On: 96,
  slider: 93,
  fxDepth: 94,
  holdSw: 95,
} as const;

/** Sample bank trigger notes A–D = C4..D#4 (MIDI 60..63, C4=60 convention). */
export const KPV_BANK_NOTES = [60, 61, 62, 63] as const;
